<template>
  <validation-observer ref="requestServiceCancelForm" v-slot="{}" tag="form">
    <div class="row">
      <div class="col-12 mb-1">
        <label class="color-gray-7 font-16 font-weight-bold">
          <i class="fas fa-times-circle color-red mr-1" />
          Razón de Cancelación:
        </label>
      </div>
      <div class="col-12 col-md-4 column-left">
        <validation-provider
          v-if="!isReadOnly"
          v-slot="{ errors }"
          name="cancelReason"
          rules="required"
        >
          <custom-drop-down
            v-model="reasonSelected"
            custom-error-msg="La razón es requerida."
            :error="errors[0]"
            fieldkey="id"
            fieldname="name"
            label="Razón:"
            :options="availableReasons"
            placeholder="Seleccione"
          />
        </validation-provider>
        <div v-else>
          <p class="data-title mb-0 title">Razón:</p>
          <p class="font-weight-normal description">
            {{ determination.reasonDescription }}
          </p>
        </div>
      </div>
      <div v-if="showCancelDocument && !isReadOnly" class="col-md-4 col-sm-12">
        <validation-provider v-slot="{ errors }" rules="required">
          <label class="data-title mb-1">Archivo:</label>
          <custom-file-input
            ref="fileUpload"
            v-model="model.document"
            accept=".pdf"
            custom-error-msg="Favor anejar el documento."
            display-style="normal"
            :error="errors[0]"
            label-position="left"
            :on-download="() => {}"
            required
          />
        </validation-provider>
      </div>
      <div v-if="isReadOnly">
        <div
          v-if="
            determinationDocument.documentFileName && showCancelDocumentOnView
          "
        >
          <p class="data-title mb-0 title">Archivo:</p>
          <div class="row">
            <div class="col-8">
              <p class="text-wrapper">
                {{ determinationDocument.documentFileName }}
              </p>
            </div>
            <div class="col-4">
              <button
                class="btn btn-outline-success mr-2 ml-2"
                type="button"
                @click="onDownloadHandler(determination.document)"
              >
                <i class="fas fa-file-download" />
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="col-12 mt-3">
        <validation-provider
          v-if="!isReadOnly"
          v-slot="{ errors }"
          name="cancelComment"
          rules="required"
        >
          <label class="data-title mb-0">Comentario:</label>
          <custom-text-area
            v-model="model.description"
            :count-classes="['float-right']"
            custom-error-msg="El comentario es requerido."
            :error="errors[0]"
            :maxlength="3000"
            show-char-count
          />
        </validation-provider>
      </div>
      <div v-if="determination.description" class="col-12">
        <p class="data-title mb-0 title">Comentario:</p>
        <p class="font-weight-normal description">
          {{ determination.description }}
        </p>
      </div>
      <div v-if="!isReadOnly" class="col-12 mt-3">
        <div class="d-flex flex-row-reverse bd-highlight w-100 actions">
          <div class="bd-highlight ml-2">
            <action-button
              i="fas  fa-save fa-lg"
              text="Guardar"
              variant="success"
              @click="saveCancel"
            />
          </div>
          <div class="mx-0 bd-highlight">
            <action-button
              id="modal-btn-cancelar"
              i="fas fa-times-circle"
              text="Cancelar"
              variant="outline-danger"
              @click="$emit('cancel')"
            />
          </div>
        </div>
      </div>
    </div>
  </validation-observer>
</template>

<script>
import objectToFormData from 'object-to-formdata';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import DeterminationReasons from '@/utils/constants/determinationReasons';
import CustomDropDown from 'custom-drop-down';
import CustomTextArea from 'CustomTextArea';
import ActionButton from 'ActionButton';
import CustomFileInput from 'CustomFileInput';
import alert from '@quisit/mipe-utils/mixins/alert.mixin.js';
import ServiceStatuses from '@/utils/constants/requestServiceStatuses';
import determinationReasons from '@/utils/constants/determinationReasons.js';
import studentStatuses from '@/utils/constants/studentStatuses.js';
import rpServiceTypes from '@/utils/constants/serviceTypes.js';
import { downloadDocumentByAttachmentId as _downloadDocumentByAttachmentId } from '@/services/common/document.api.js';

import {
  getDisciplineTypesForSaee,
  saveDetermination,
  getDeterminationRequestService,
} from '@/services/uspqrp/provisional-remedy.api';

const MIN_DAYS_COUNT = 120;

export default {
  name: 'RequestServiceCancel',
  components: {
    CustomDropDown,
    CustomTextArea,
    ActionButton,
    CustomFileInput,
    ValidationProvider,
    ValidationObserver,
  },
  mixins: [alert],
  props: {
    data: {
      type: Object,
      required: true,
      default: () => {},
    },
    isReadOnly: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      model: {
        description: null,
      },
      reasonSelected: null,
      determination: {
        requestServiceId: null,
        serviceStatusDescription: null,
        reasonDescription: null,
        judgeName: null,
        description: null,
        objection: null,
        document: null,
      },
      disciplineTypeValidForSaeeAndRP: [],
    };
  },
  computed: {
    showCancelDocument() {
      return [
        DeterminationReasons.RejectToReceiveServiceWithSAEE,
        DeterminationReasons.TransferByOffering,
        DeterminationReasons.High,
        DeterminationReasons.RejectOfCompensatoryServices,
        DeterminationReasons.RefusalOfParentsService,
      ].includes(this.reasonSelected?.id);
    },
    showCancelDocumentOnView() {
      return [
        DeterminationReasons.RejectToReceiveServiceWithSAEE,
        DeterminationReasons.TransferByOffering,
        DeterminationReasons.High,
        DeterminationReasons.RejectOfCompensatoryServices,
        DeterminationReasons.RefusalOfParentsService,
      ].includes(this.determination.reasonId);
    },
    availableReasons() {
      const {
        daysCountWithoutContract,
        hasActiveContract,
        compensatoryMaxVisitCount,
        serviceTypeId,
        disciplineTypeId,
      } = this.data;

      const discipline = this.disciplineTypeValidForSaeeAndRP.find(
        (d) => d.id == disciplineTypeId
      );

      return this.data.reasons.filter((x) => {
        if (
          x.id === determinationReasons.Others &&
          this.userHasPermissions(
            'menu.rempro.view.reasonforothersincancellation'
          )
        ) {
          return true;
        }
        //Only return the reason ReturnedToSAEEByHiringLack if the student has not an active contract,
        //the daysCountWithoutContract is greater than 120,
        // the discipline is Therapy or Evaluation and the compensatoryMaxVisitCount is null
        if (
          x.id == DeterminationReasons.ReturnedToSAEEByHiringLack &&
          !hasActiveContract &&
          daysCountWithoutContract >= MIN_DAYS_COUNT &&
          discipline &&
          (serviceTypeId == rpServiceTypes.Therapy ||
            serviceTypeId == rpServiceTypes.Evaluation) &&
          !compensatoryMaxVisitCount
        ) {
          return true;
        }

        if (
          x.id != DeterminationReasons.ReturnedToSAEEByHiringLack &&
          x.id != determinationReasons.Others
        ) {
          return true;
        } else {
          return false;
        }
      });
    },
    determinationDocument() {
      return {
        ...this.determination.document,
        documentFileName: this.determination.document?.documentFileName,
      };
    },
  },
  mounted() {
    if (this.isReadOnly) {
      this.getDeterminationRequestService();
    }
    this.getDisciplineTypeValidForSaeeAndRP();
  },
  methods: {
    async saveCancel() {
      const isFormValid = await this.$refs.requestServiceCancelForm.validate();
      if (!isFormValid) {
        this.ShowToast(
          'Alerta',
          'Favor completar la información de cancelación solicitada.',
          'error'
        );
        return;
      }

      const answer = await this.ShowCancelModal(
        'Confirmación',
        `¿Está seguro que desea Cancelar el servicio ${this.data.serviceDiscipline}?`,
        'Sí',
        'No'
      );
      if (!answer) return;

      if (
        this.reasonSelected.id ==
          DeterminationReasons.ReturnedToSAEEByHiringLack &&
        [
          studentStatuses.Egress,
          studentStatuses.Inactive,
          studentStatuses.NotEligible,
        ].includes(this.data.studentStatusCode)
      ) {
        this.ShowToast(
          'Alerta',
          `Estudiante se encuentra en estatus ${this.data.studentStatus} no es posible transferir a la SAEE`,
          'error'
        );
        return;
      }

      const payload = objectToFormData(
        {
          requestServiceId: this.data.requestServiceId,
          serviceStatusId: ServiceStatuses.Cancelled,
          reasonId: this.reasonSelected.id,
          ...this.model,
        },
        { indices: true, nullsAsUndefineds: true }
      );

      const { data } = await saveDetermination(payload).catch((err) => {
        this.ShowToast('Alerta', err.response.data.message, 'error');
        return { data: false };
      });

      if (data)
        this.ShowToast(
          `Confirmación`,
          'El servicio ha sido cancelado exitosamente.',
          'success'
        );

      this.$emit('save', data);
    },
    async getDeterminationRequestService() {
      const { data } = await getDeterminationRequestService(
        this.data.requestServiceId
      ).catch((error) => {
        this.ShowToast('Alerta', `${error.message}.`, 'error');
        return { data: false };
      });

      if (data) {
        this.determination = data;
      }
    },
    async getDisciplineTypeValidForSaeeAndRP() {
      const { data } = await getDisciplineTypesForSaee(this.data.serviceTypeId);
      this.disciplineTypeValidForSaeeAndRP = [...data];
    },
    onDownloadHandler({ documentId }) {
      if (documentId) {
        _downloadDocumentByAttachmentId(documentId);
      }
    },
  },
  watch: {
    isReadOnly(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.getDeterminationRequestService();
      }
    },
  },
};
</script>
<style scoped lang="scss">
.column-left {
  max-width: 196.7px !important;
}
.title {
  font-size: 14px;
}
.description {
  font-size: 16px;
}
.text-wrapper {
  word-break: break-all;
}
</style>
